import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CoursePricing {
  id: string;
  state_code: string;
  course_type: string;
  year_level_min: number;
  year_level_max: number;
  price_per_session: number;
  total_sessions: number;
  books_cost: number;
  center_id: string | null;
}

export const useCoursePricing = (stateCode: string, courseType: string, centerId?: string) => {
  const { data: pricing = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: ['coursePricing', stateCode, courseType, centerId],
    staleTime: 10 * 60 * 1000, // 10 minutes
    queryFn: async () => {
      // First, try center-specific pricing
      if (centerId) {
        const { data: centerPricing, error: centerError } = await supabase
          .from('course_pricing')
          .select('id, state_code, course_type, year_level_min, year_level_max, price_per_session, total_sessions, books_cost, center_id')
          .eq('center_id', centerId)
          .eq('course_type', courseType)
          .order('year_level_min');

        if (centerError) throw centerError;
        if (centerPricing && centerPricing.length > 0) return centerPricing;
      }

      // Fall back to state-level pricing
      const { data, error } = await supabase
        .from('course_pricing')
        .select('id, state_code, course_type, year_level_min, year_level_max, price_per_session, total_sessions, books_cost, center_id')
        .eq('state_code', stateCode)
        .eq('course_type', courseType)
        .is('center_id', null)
        .order('year_level_min');

      if (error) throw error;
      return data || [];
    },
  });

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to fetch pricing') : null;

  const getPricingForYearRange = (minYear: number, maxYear: number) => {
    return pricing.find(p => 
      p.year_level_min <= minYear && p.year_level_max >= maxYear
    );
  };

  return { pricing, loading, error, getPricingForYearRange };
};
